import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { signOut } from '@/lib/auth-client';

export const Route = createFileRoute('/no-access')({
  component: NoAccessPage,
});

/**
 * Terminal "you can't get in" screen. Reached when a user has a valid Google
 * session but no usable access to the app: no provisioned domain row, a blocked
 * account (inactive / terminated / not yet started), or a role that grants zero
 * accessible pages. Deliberately NOT gated by session/permission checks so it
 * can never bounce — routing a stuck user here is what breaks the old
 * /login ⇄ /timesheets redirect loop.
 */
function NoAccessPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleSignOut = async (): Promise<void> => {
    await signOut();
    queryClient.clear();
    await navigate({ to: '/login' });
  };

  return (
    <div className="grid min-h-screen place-items-center px-4">
      <div className="w-full max-w-md space-y-6 rounded-lg border border-border bg-background p-8 text-center shadow-sm">
        <div className="mx-auto w-fit rounded-full bg-muted p-3">
          <Lock className="size-6 text-muted-foreground" aria-hidden />
        </div>
        <div className="space-y-1">
          <h1 className="text-xl font-semibold">No access</h1>
          <p className="text-sm text-muted-foreground">
            Your account doesn't have access to Clockwise. If you think this is a mistake — for
            example your account is inactive or hasn't been set up yet — contact your administrator.
          </p>
        </div>
        <Button variant="outline" className="w-full" onClick={handleSignOut}>
          Log out
        </Button>
      </div>
    </div>
  );
}
